import React, { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import ListSeparator from '../../../components/envago/ListSeparator/ListSeparator';
import { JobListItemIconType } from '../../../components/envago/JobListItem/JobListItem';
import JobListItemWrapper from '../JobListItemWrapper';
import { FieldVisibility } from '../../../fieldVisibility/fieldVisibility';
import { useFieldVisibility } from '../../../fieldVisibility/useFieldVisibility';
import { JobType } from '../../../redux/jobs/jobs.types';
import { jobsSelectors } from '../../../redux/jobs';
import { EnvagoAria } from '../../../aria.types';

// Slot heights: rendered element height plus the 8px gap that the plain flex
// column applies between its children. Content keeps its natural height inside
// the slot, so a shorter element (a job without an address line, for instance)
// leaves the remainder as whitespace rather than being stretched.
const LIST_GAP_PX = 8;
const JOB_ROW_HEIGHT = 76 + LIST_GAP_PX;
const SEPARATOR_ROW_HEIGHT = 28 + LIST_GAP_PX;

// Below this row count the plain (non-virtualized) DOM path is used. Both list
// screens share the threshold so they never diverge in behaviour.
const VIRTUALIZATION_THRESHOLD = 100;

// Height forced onto the list while measuring, so the scrollable ancestor is
// certainly clamped at its maxHeight. See the measurement effect below.
const OVERSHOOT_PROBE_PX = 100000;
const MIN_LIST_HEIGHT = 120;

export type JobRow =
    | {
          kind: 'job';
          key: string;
          job: JobType;
          isGhost?: boolean;
          isDone?: boolean;
          iconRight?: JobListItemIconType;
          aria?: EnvagoAria;
      }
    | { kind: 'separator'; key: string; text: string; isActive?: boolean; aria?: EnvagoAria }
    // Escape hatch for the non-job entries (AI assistant entry point, "add
    // unknown reading"). height is the rendered element height without the gap.
    | { kind: 'node'; key: string; height: number; node: ReactNode };

interface RowRenderData {
    rows: JobRow[];
    activeJobId?: string;
    getOnClick: (jobId: string) => () => void;
    hasObjectKeyMultipleValues: boolean;
    hasLocationDescriptionMultipleValues: boolean;
    visibility: FieldVisibility;
}

const rowSlotHeight = (row: JobRow): number => {
    switch (row.kind) {
        case 'job':
            return JOB_ROW_HEIGHT;
        case 'separator':
            return SEPARATOR_ROW_HEIGHT;
        case 'node':
            return row.height + LIST_GAP_PX;
    }
};

const RowContent = ({ row, data }: { row: JobRow; data: RowRenderData }) => {
    if (row.kind === 'separator') {
        return <ListSeparator text={row.text} isActive={row.isActive} aria={row.aria} />;
    }
    if (row.kind === 'node') {
        return <>{row.node}</>;
    }
    return (
        <JobListItemWrapper
            job={row.job}
            onClick={data.getOnClick(row.job.id)}
            isActive={data.activeJobId === row.job.id}
            isGhost={row.isGhost}
            isDone={row.isDone}
            iconRight={row.iconRight}
            aria={row.aria}
            hasObjectKeyMultipleValues={data.hasObjectKeyMultipleValues}
            hasLocationDescriptionMultipleValues={data.hasLocationDescriptionMultipleValues}
            visibility={data.visibility}
        />
    );
};

const VirtualRow = ({ index, style, data }: ListChildComponentProps<RowRenderData>) => {
    const row = data.rows[index];
    // The gap lives inside the slot as padding so absolute positioning keeps the
    // same visual rhythm as the flex column it replaces.
    return (
        <div style={style}>
            <div style={{ paddingBottom: LIST_GAP_PX }}>
                <RowContent row={row} data={data} />
            </div>
        </div>
    );
};

const findScrollableAncestor = (start: HTMLElement | null): HTMLElement | null => {
    let el: HTMLElement | null = start;
    while (el) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return el;
        el = el.parentElement;
    }
    return null;
};

interface IJobRowListProps {
    rows: JobRow[];
    /** Route prefix for item navigation, e.g. '/ablesung' or '/auswertung'. */
    routeBasePath: string;
    activeJobId?: string;
}

/**
 * Renders a job list, switching to react-window virtualization above
 * VIRTUALIZATION_THRESHOLD rows. Both the reading and the evaluation screen go
 * through here so the two stay visually and behaviourally identical.
 *
 * The multiple-value flags are read here rather than in JobListItemWrapper:
 * the selector factory used to run once per list item (16.000 executions per
 * store notification at 8000 jobs). Reading them once at list level and passing
 * them down keeps the memoisation intact — and doing it in this shared
 * component means no caller can forget to pass them.
 *
 * Die Feld-Sichtbarkeit wird aus demselben Grund hier gelesen und
 * hinuntergereicht: ein useFieldVisibility() je Zeile waeren 8000
 * Store-Abonnements fuer eine Angabe, die sich zwischen den Zeilen nie
 * unterscheidet.
 */
const JobRowList = ({ rows, routeBasePath, activeJobId }: IJobRowListProps) => {
    const dispatch = useDispatch();
    const { hasObjectKeyMultipleValues, hasLocationDescriptionMultipleValues } = useSelector(
        jobsSelectors.getJobListMultipleValueFlags,
    );
    const visibility = useFieldVisibility();

    // Stable per-jobId onClick handlers. Inline arrows would hand each memoised
    // item a fresh callback on every render and defeat React.memo entirely.
    const onClickByIdRef = useRef<Record<string, () => void>>({});
    const getOnClick = useCallback(
        (jobId: string) => {
            if (!onClickByIdRef.current[jobId]) {
                onClickByIdRef.current[jobId] = () => dispatch(push(`${routeBasePath}/${jobId}`));
            }
            return onClickByIdRef.current[jobId];
        },
        [dispatch, routeBasePath],
    );

    const shouldVirtualize = rows.length > VIRTUALIZATION_THRESHOLD;

    const itemData = useMemo<RowRenderData>(
        () => ({
            rows,
            activeJobId,
            getOnClick,
            hasObjectKeyMultipleValues,
            hasLocationDescriptionMultipleValues,
            visibility,
        }),
        [rows, activeJobId, getOnClick, hasObjectKeyMultipleValues, hasLocationDescriptionMultipleValues, visibility],
    );

    // The sidebar these lists live in is position:fixed with only a maxHeight
    // (no height), so it grows with its content — an inner flex-1 measures 0
    // there and a viewport-derived height overshoots the maxHeight and adds a
    // second scrollbar.
    //
    // Measuring the ancestor directly is circular: our own height feeds into
    // its height, so each measurement pass grew the list by the ancestor's
    // padding until maxHeight was reached — visible as the list animating open.
    // Instead force our height far beyond the limit for the duration of the
    // measurement. The ancestor is then guaranteed to be clamped at its
    // maxHeight, which is independent of our content, making the reading stable.
    const listContainerRef = useRef<HTMLDivElement>(null);
    const [listHeight, setListHeight] = useState(0);

    useLayoutEffect(() => {
        if (!shouldVirtualize) return;

        const update = () => {
            const el = listContainerRef.current;
            if (!el) return;
            const ancestor = findScrollableAncestor(el.parentElement);
            if (!ancestor) {
                setListHeight(Math.max(MIN_LIST_HEIGHT, Math.floor(window.innerHeight - el.getBoundingClientRect().top)));
                return;
            }

            const previousHeight = el.style.height;
            el.style.height = `${OVERSHOOT_PROBE_PX}px`;
            // Reading layout here flushes the overshoot, so clientHeight is the
            // ancestor's clamped maxHeight rather than its content height.
            const limit = ancestor.clientHeight;
            const offsetInsideAncestor =
                el.getBoundingClientRect().top - ancestor.getBoundingClientRect().top + ancestor.scrollTop;
            el.style.height = previousHeight;

            setListHeight(Math.max(MIN_LIST_HEIGHT, Math.floor(limit - offsetInsideAncestor)));
        };

        update();
        window.addEventListener('resize', update);

        const ancestor = findScrollableAncestor(listContainerRef.current?.parentElement ?? null);
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : undefined;
        if (observer && ancestor) observer.observe(ancestor);

        return () => {
            window.removeEventListener('resize', update);
            observer?.disconnect();
        };
    }, [shouldVirtualize]);

    // VariableSizeList caches offsets by index; the cache has to be dropped
    // whenever the row composition changes or rows keep their stale heights.
    const virtualListRef = useRef<VariableSizeList>(null);
    useLayoutEffect(() => {
        virtualListRef.current?.resetAfterIndex(0, false);
    }, [rows]);

    const getItemSize = useCallback((index: number) => rowSlotHeight(rows[index]), [rows]);

    if (shouldVirtualize) {
        return (
            <div ref={listContainerRef} style={{ height: listHeight, width: '100%' }}>
                {listHeight > 0 && (
                    <VariableSizeList
                        ref={virtualListRef}
                        height={listHeight}
                        width={'100%'}
                        itemCount={rows.length}
                        itemSize={getItemSize}
                        // Without this, unmeasured rows are assumed to be 50px
                        // (react-window's default), so the scroll height starts
                        // far too small and grows while scrolling — the thumb
                        // jumps around. Job rows dominate every list, so their
                        // height is the right estimate.
                        estimatedItemSize={JOB_ROW_HEIGHT}
                        itemKey={(index) => rows[index].key}
                        itemData={itemData}
                        overscanCount={4}
                        // react-window sets overflow:auto on both axes. The active
                        // card is shifted right by translate-x-1, which then shows
                        // up as a horizontal scrollbar. The sidebar used to clip
                        // that via overflow-x-hidden, so clip it here too.
                        style={{ overflowX: 'hidden' }}
                    >
                        {VirtualRow}
                    </VariableSizeList>
                )}
            </div>
        );
    }

    return (
        <div className={'flex flex-col gap-2'}>
            {rows.map((row) => (
                <RowContent key={row.key} row={row} data={itemData} />
            ))}
        </div>
    );
};

export default JobRowList;
